import { differenceInSeconds } from "date-fns";
import { useEffect, useState } from "react";

import { useAutoTrading } from "./useAutoTrading";

// Helper function to format timer
const formatTimer = (startedAt: string): string => {
  // Fix malformed ISO 8601 date format (remove duplicate timezone info)
  // API returns: "2025-12-30T15:41:17.806170+00:00Z" (invalid)
  // Should be: "2025-12-30T15:41:17.806170+00:00" or "2025-12-30T15:41:17.806170Z"
  const cleanedDate = startedAt.replace(/\+00:00Z$/, "Z");

  const seconds = differenceInSeconds(new Date(), new Date(cleanedDate));

  // Handle invalid dates
  if (isNaN(seconds)) {
    return "00:00:00";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Hook to get auto-trading timer value
 * Returns formatted timer string (HH:MM:SS) when auto-trading is running,
 * undefined otherwise
 */
export function useAutoTradingTimer(): string | undefined {
  const { status, isRunning } = useAutoTrading();
  const [timerTick, setTimerTick] = useState(0);

  const autoTradingStartedAt = isRunning ? status?.started_at : null;

  useEffect(() => {
    if (!autoTradingStartedAt) {
      return;
    }

    const interval = setInterval(() => {
      setTimerTick((tick) => tick + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [autoTradingStartedAt]);

  // Calculate timer value (timerTick is used to force re-render every second)
  const timer = autoTradingStartedAt ? formatTimer(autoTradingStartedAt) : undefined;
  // Suppress unused warning for timerTick - it's used to trigger re-renders
  void timerTick;

  return timer;
}
